import { f0FormField, RenderIfCondition } from '@factorialco/f0-react'
import { F0SelectItemObject } from '@factorialco/f0-react'
import { z } from 'zod'

import { LocationRecord, useLocationSelectorSource } from './source'

type BaseLocationSelectorFormFieldParams = {
  label: string
  section: string
  placeholder?: string
  helpText?: string
  renderIf?: RenderIfCondition
}

const mapLocationOptions = (
  location: LocationRecord
): F0SelectItemObject<number, LocationRecord> => ({
  value: location.id,
  label: location.name,
  item: location,
})

// Single select - optional
const useOptionalSingleLocationSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLocationSelectorFormFieldParams) => {
  const schema = z.number().optional()

  const source = useLocationSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    showSearchBox: true,
    source,
    mapOptions: mapLocationOptions,
  })
}

// Single select - mandatory
const useMandatorySingleLocationSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLocationSelectorFormFieldParams) => {
  const schema = z.number()

  const source = useLocationSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    showSearchBox: true,
    source,
    mapOptions: mapLocationOptions,
  })
}

// Multi select - optional
const useOptionalMultiLocationSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLocationSelectorFormFieldParams) => {
  const schema = z.array(z.number()).optional()

  const source = useLocationSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    showSearchBox: true,
    multiple: true,
    source,
    mapOptions: mapLocationOptions,
  })
}

// Multi select - mandatory
const useMandatoryMultiLocationSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BaseLocationSelectorFormFieldParams) => {
  const schema = z.array(z.number()).min(1)

  const source = useLocationSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    showSearchBox: true,
    multiple: true,
    source,
    mapOptions: mapLocationOptions,
  })
}

type SingleLocationSelectorFormFieldParams<M extends boolean> =
  BaseLocationSelectorFormFieldParams & { mandatory: M }

export const useSingleLocationSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: SingleLocationSelectorFormFieldParams<M>) => {
  const mandatorySingleLocationSelectorFormField = useMandatorySingleLocationSelectorFormField({
    ...rest,
    renderIf,
  })
  const optionalSingleLocationSelectorFormField = useOptionalSingleLocationSelectorFormField({
    ...rest,
    renderIf,
  })

  return mandatory
    ? mandatorySingleLocationSelectorFormField
    : optionalSingleLocationSelectorFormField
}

type MultiLocationSelectorFormFieldParams<M extends boolean> =
  BaseLocationSelectorFormFieldParams & { mandatory: M }

export const useMultiLocationSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiLocationSelectorFormFieldParams<M>) => {
  const mandatoryMultiLocationSelectorFormField = useMandatoryMultiLocationSelectorFormField({
    ...rest,
    renderIf,
  })
  const optionalMultiLocationSelectorFormField = useOptionalMultiLocationSelectorFormField({
    ...rest,
    renderIf,
  })

  return mandatory
    ? mandatoryMultiLocationSelectorFormField
    : optionalMultiLocationSelectorFormField
}
